import React, { useCallback, useEffect, useRef, useState } from "react";
import { ConnectedProps, connect } from "react-redux";
import { ReducerState, WifiConnectArgs } from "../../types";
import { useGetDeviceByIdQuery } from "../../actions/devicesInQuery";
import { Modal, Button, Form, Typography, message } from "antd";
import { useAppDispatch, useAppSelector } from "../../hooks/reduxHook";
import { setWifiModalOpen } from "../../reducers/devicesSlice";
import { ReactComponent as BluetoothIcon } from "../../assets/icons/bluetooth.svg";
import DeviceSetupForm from "./DeviceSetupForm";
import WifiSuccessMessage from "./WifiSuccessMessage";
import { BLUETOOTH_STATES } from "../../providers/bluetoothProvider";
import { useDeviceWifiUpdateMutation } from "../../actions/devicesInQuery";
import "./index.css";

const { Text } = Typography;

// Automatic scans after pairing before giving up and letting the user type the network name.
export const MAX_AUTO_SCAN_ATTEMPTS = 3;
export const SCAN_FAILED_MESSAGE =
  "Could not load Wi-Fi networks from the trap. Type the network name, or press Refresh to try again.";

const mapStateToProps = (state: ReducerState) => {
  return {
    selected: state.devices?.selected?.uuid || null,
  };
};

const connector = connect(mapStateToProps, {});

type Props = ConnectedProps<typeof connector> & {
  disconnect: () => void;
  connected: boolean;
  bluetoothState: string;
  scanWifi: () => Promise<string[] | null>;
  handleConnect: (deviceUuid: string) => void;
  startWifiConnect: (ssid: string, psk: string) => void;
  isWifiConnected: boolean;
  currentNetwork: string;
};

const Index: React.FC<Props> = ({
  selected,
  connected,
  disconnect,
  bluetoothState,
  scanWifi,
  startWifiConnect,
  isWifiConnected,
  currentNetwork,
  handleConnect,
}) => {
  const dispatch = useAppDispatch();
  const { data: selectedDevice } = useGetDeviceByIdQuery(selected!, {
    skip: !selected,
  });
  const isModalOpen = useAppSelector((state) => state.devices.isWifiModalOpen);
  const [messageApi, contextHolder] = message.useMessage();
  const [form] = Form.useForm<WifiConnectArgs>();
  const ssid = Form.useWatch("ssid", form);
  const [targetWifi, setTargetWifi] = useState<string>("");
  const [deviceWifiUpdate] = useDeviceWifiUpdateMutation();
  const [wifiList, setWifiList] = useState<string[]>([]);
  const [isConnecting, setIsConnecting] = useState(false);
  const [isPaired, setIsPaired] = useState(false);
  const [autoScanAttempts, setAutoScanAttempts] = useState(0);
  const scanInFlightRef = useRef(false);
  // Bumped on every disconnect so a scan still in flight cannot touch the next pairing.
  const pairingRef = useRef(0);
  // scanWifi is a new function on every parent render; reading it through a ref keeps the
  // auto-scan effect from restarting its delay each time.
  const scanWifiRef = useRef(scanWifi);
  scanWifiRef.current = scanWifi;

  useEffect(() => {
    if (bluetoothState === BLUETOOTH_STATES.CONNECTED) {
      setIsPaired(true);
    } else if (bluetoothState === BLUETOOTH_STATES.DISCONNECTED) {
      // The next pairing may be a different trap, so neither its list nor our give-up count carry over.
      pairingRef.current += 1;
      setWifiList([]);
      setAutoScanAttempts(0);
    }
  }, [bluetoothState]);

  // Returns whether the scan produced a usable list, or null when the pairing ended before it settled.
  const runScan = useCallback(async () => {
    if (scanInFlightRef.current) {
      return false;
    }
    const pairing = pairingRef.current;
    scanInFlightRef.current = true;
    setIsConnecting(true);
    try {
      const ssids = await scanWifiRef.current();
      if (pairing !== pairingRef.current) {
        return null;
      }
      if (ssids !== null) {
        setWifiList(ssids);
      }
      return !!ssids?.length;
    } finally {
      scanInFlightRef.current = false;
      setIsConnecting(false);
    }
  }, []);

  const onFinishFormSubmit = async (values: WifiConnectArgs) => {
    setTargetWifi(values.ssid);
    startWifiConnect(values.ssid, values.psk);
  };

  const handleBluetoothDisconnection = useCallback(() => {
    if (bluetoothState === BLUETOOTH_STATES.DISCONNECTED) {
      dispatch(setWifiModalOpen(false));
    }
  }, [bluetoothState, dispatch]);

  useEffect(() => {
    handleBluetoothDisconnection();
  }, [bluetoothState, handleBluetoothDisconnection]);

  useEffect(() => {
    if (connected && targetWifi.length > 0 && targetWifi === currentNetwork) {
      //connect to wifi success
      messageApi.open({
        content: (
          <WifiSuccessMessage
            title="Device connected:"
            description={selectedDevice?.display_name || ""}
            onClose={() => messageApi.destroy()}
          />
        ),
        duration: 0,
        className: "success-wifi-connect-holder",
      });
      deviceWifiUpdate({
        deviceUuid: selectedDevice?.uuid || "",
        wifi: targetWifi,
      });
      setIsPaired(false);
      setTargetWifi("");
      disconnect();
      dispatch(setWifiModalOpen(false));
    }
  }, [
    targetWifi,
    messageApi,
    selectedDevice,
    currentNetwork,
    deviceWifiUpdate,
    dispatch,
    disconnect,
    connected,
  ]);

  useEffect(() => {
    if (
      bluetoothState !== BLUETOOTH_STATES.CONNECTED ||
      wifiList.length > 0 ||
      isConnecting ||
      autoScanAttempts >= MAX_AUTO_SCAN_ATTEMPTS
    ) {
      return;
    }
    const timer = setTimeout(async () => {
      const found = await runScan();
      if (found === false) {
        const attempts = autoScanAttempts + 1;
        setAutoScanAttempts(attempts);
        if (attempts >= MAX_AUTO_SCAN_ATTEMPTS) {
          messageApi.warning(SCAN_FAILED_MESSAGE);
        }
      }
    }, 1000);
    return () => clearTimeout(timer);
  }, [
    bluetoothState,
    wifiList,
    isConnecting,
    autoScanAttempts,
    runScan,
    messageApi,
  ]);

  // actions
  const onScanButtonPressed = async () => {};

  const onCancelButtonPressed = async () => {
    disconnect();
    form.resetFields();
    messageApi.destroy();
  };

  const onRescanButtonPressed = async () => {
    if (wifiList.length === 0) {
      // Hand an empty list back to the automatic scans for another full set of attempts.
      setAutoScanAttempts(0);
      return;
    }
    if ((await runScan()) === false) {
      messageApi.warning(SCAN_FAILED_MESSAGE);
    }
  };

  const handleModalSubmit = () => {
    if (isPaired) {
      form.submit();
    } else {
      handleConnect(selectedDevice?.uuid || "");
    }
  };

  return (
    <Modal
      open={isModalOpen}
      onCancel={() => {
        onCancelButtonPressed();
        setIsPaired(false);
        dispatch(setWifiModalOpen(false));
      }}
      onOk={() => {
        handleModalSubmit();
      }}
      styles={{
        content: {
          borderRadius: ".5rem",
          border: "1px solid #0C4A6E",
        },
      }}
      closeIcon={null}
      okText={
        selectedDevice?.wifi && selectedDevice?.wifi?.length > 0
          ? "Change Wifi"
          : "Connect"
      }
      cancelButtonProps={{
        className: "btn-wifi-cancel",
      }}
      okButtonProps={{
        className: "btn-wifi-confirm",
        disabled:
          (bluetoothState !== BLUETOOTH_STATES.CONNECTED &&
            bluetoothState !== BLUETOOTH_STATES.DISCONNECTED) ||
          (bluetoothState === BLUETOOTH_STATES.CONNECTED &&
            (!ssid?.length || currentNetwork === ssid)),
      }}
    >
      {contextHolder}
      <div>
        <div className="mb-4">
          <Text className="text-[24px]! font-medium! wrap-anywhere">
            Device: {selectedDevice?.display_name || ""}
          </Text>
        </div>
        {selectedDevice?.wifi && selectedDevice?.wifi?.length > 0 && (
          <div className="mb-2">
            <Text className="text-[16px]!">
              Connected Wifi: {selectedDevice?.wifi}
            </Text>
          </div>
        )}
        {bluetoothState === BLUETOOTH_STATES.UNSUPPORTED ? (
          <h3>
            This browser does not support WebBluetooth. Please use a supported
            browser such as Google Chrome for this feature.
          </h3>
        ) : (
          <div className="w-full flex items-center justify-center">
            {bluetoothState === BLUETOOTH_STATES.DISCONNECTED && (
              <Button
                onClick={onScanButtonPressed}
                type="primary"
                shape="round"
                about="Scan"
                className="device-setup-scan-button"
                icon={<BluetoothIcon className="bluetooth-icon" fill="white" />}
                block
              />
            )}
            <DeviceSetupForm
              form={form}
              bluetoothState={bluetoothState}
              isWifiConnected={isWifiConnected || !!currentNetwork}
              handleFinish={onFinishFormSubmit}
              handleRescan={onRescanButtonPressed}
              wifiList={wifiList}
              currentNetwork={currentNetwork}
            />
          </div>
        )}
      </div>
    </Modal>
  );
};

export default connector(Index);
